import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import CloseButton from './CloseButton';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { CURRENCY } from '../config/currency';

interface SearchCriteria {
  value: string;
  label: string;
  placeholder: string;
}

interface SearchResult {
  id: number;
  type: 'order' | 'kot';
  orderNumber?: string;
  kotNumber?: string;
  customerName?: string;
  tableName?: string;
  phone?: string;
  status: string;
  total?: number;
  createdAt: string;
  items?: string[];
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCriteria, setSelectedCriteria] = useState('orderNumber');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  const searchCriteria: SearchCriteria[] = [
    { value: 'orderNumber', label: 'Order Number', placeholder: 'Search by order number (e.g., D-20251014-015)...' },
    { value: 'kotNumber', label: 'KOT Number', placeholder: 'Search by KOT number...' },
    { value: 'customerName', label: 'Customer Name', placeholder: 'Search by customer name...' },
    { value: 'tableNumber', label: 'Table Number', placeholder: 'Search by table number...' },
    { value: 'phoneNumber', label: 'Phone Number', placeholder: 'Search by phone number...' }
  ];

  const currentCriteria = searchCriteria.find(c => c.value === selectedCriteria) || searchCriteria[0];

  // Perform search
  const performSearch = useCallback(async (query: string, criteria: string) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get<SearchResult[]>('/api/search', {
        params: {
          query: query.trim(),
          criteria,
          limit: 10
        }
      });
      setResults(response.data);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      performSearch(searchQuery, selectedCriteria);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, selectedCriteria, performSearch]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && results.length > 0) {
        e.preventDefault();
        handleResultClick(results[selectedIndex]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'order') {
      navigate(`/vendor/orders/${result.id}`);
    } else if (result.type === 'kot') {
      navigate(`/vendor/kot/${result.id}`);
    }
    onClose();
  };

  const formatTime = (dateString: string) => {
    // Handle both ISO strings (with timezone) and LocalDateTime strings (without timezone)
    // If the string doesn't contain 'Z' or timezone offset, treat it as UTC
    let date: Date;
    if (dateString && !dateString.includes('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
      // LocalDateTime format from search API - append 'Z' to treat as UTC
      date = new Date(dateString + 'Z');
    } else {
      // ISO format with timezone from orders API
      date = new Date(dateString);
    }
    
    return date.toLocaleString('en-US', { 
      month: 'numeric', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  };

  const getStatusColor = (status: string) => {
    const statusTheme = theme.components?.status;
    const colors: { [key: string]: string } = {
      PENDING: statusTheme?.PENDING?.text || theme.warning || '#F59E0B',
      ACCEPTED: statusTheme?.ACCEPTED?.text || theme.accent || '#3B82F6',
      PREPARING: statusTheme?.PREPARING?.text || theme.accent || '#8B5CF6',
      READY: statusTheme?.READY?.text || theme.success || '#10B981',
      COMPLETED: statusTheme?.COMPLETED?.text || theme.success || '#059669',
      CANCELLED: statusTheme?.CANCELLED?.text || theme.danger || '#EF4444'
    };
    return colors[status] || theme.gray[500];
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-start justify-center z-[101] pt-[10vh]">
        <div
          className="w-full max-w-2xl mx-2 sm:mx-4 rounded-lg shadow-2xl overflow-hidden"
          style={{
            backgroundColor: theme.components.card.background,
            borderColor: theme.components.card.border,
            border: '1px solid'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Header */}
          <div className="flex items-center gap-2 p-3 sm:p-4 border-b" style={{ borderColor: theme.components.card.border }}>
            {/* Search Criteria Dropdown */}
            <select
              value={selectedCriteria}
              onChange={(e) => setSelectedCriteria(e.target.value)}
              className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md border outline-none flex-shrink-0"
              style={{
                backgroundColor: theme.components.card.background,
                color: theme.text,
                borderColor: theme.components.card.border,
                minWidth: '110px',
                maxWidth: '140px'
              }}
            >
              {searchCriteria.map(criteria => (
                <option key={criteria.value} value={criteria.value}>
                  {criteria.label}
                </option>
              ))}
            </select>

            {/* Search Input */}
            <div className="flex-1 relative min-w-0">
              <MagnifyingGlassIcon
                className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5"
                style={{ color: theme.gray[400] }}
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={currentCriteria.placeholder}
                className="w-full pl-8 sm:pl-10 pr-2 sm:pr-4 py-2 text-xs sm:text-sm rounded-md border outline-none"
                style={{
                  backgroundColor: theme.components.card.background,
                  color: theme.text,
                  borderColor: theme.components.card.border
                }}
              />
            </div>

            {/* Close Button */}
            <CloseButton onClick={onClose} size="md" />
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {isSearching ? (
              <div className="p-8 text-center" style={{ color: theme.gray[500] }}>
                <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-2" style={{ borderColor: theme.primary }} />
                <p className="text-sm">Searching...</p>
              </div>
            ) : results.length > 0 ? (
              <div className="py-2">
                {results.map((result, index) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className="w-full px-4 py-3 text-left transition-colors"
                    style={{
                      backgroundColor: selectedIndex === index ? theme.primary + '15' : 'transparent',
                      borderLeft: selectedIndex === index ? `3px solid ${theme.primary}` : '3px solid transparent'
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Order/KOT Number */}
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">📋</span>
                          <span className="font-semibold" style={{ color: theme.text }}>
                            {result.orderNumber || result.kotNumber}
                          </span>
                          {result.tableName && (
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: theme.accent + '20', color: theme.accent }}>
                              {result.tableName}
                            </span>
                          )}
                        </div>

                        {/* Customer & Details */}
                        <div className="flex items-center gap-3 text-sm" style={{ color: theme.gray[600] }}>
                          {result.customerName && (
                            <span>👤 {result.customerName}</span>
                          )}
                          {result.phone && (
                            <span>📞 {result.phone}</span>
                          )}
                          <span>{formatTime(result.createdAt)}</span>
                        </div>

                        {/* Items preview */}
                        {result.items && result.items.length > 0 && (
                          <div className="text-xs mt-1 truncate" style={{ color: theme.gray[500] }}>
                            {result.items.slice(0, 3).join(', ')}
                            {result.items.length > 3 && ` +${result.items.length - 3} more`}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        {/* Status Badge */}
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{
                            backgroundColor: getStatusColor(result.status) + '20',
                            color: getStatusColor(result.status)
                          }}
                        >
                          {result.status}
                        </span>

                        {/* Total */}
                        {result.total && (
                          <span className="text-sm font-semibold" style={{ color: theme.text }}>
                            {CURRENCY.symbol}{result.total.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : searchQuery.trim() ? (
              <div className="p-8 text-center" style={{ color: theme.gray[500] }}>
                <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No results found for "{searchQuery}"</p>
              </div>
            ) : (
              <div className="p-8 text-center" style={{ color: theme.gray[500] }}>
                <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Start typing to search...</p>
                <p className="text-xs mt-2 opacity-75">
                  Press <kbd className="px-1.5 py-0.5 rounded border" style={{ borderColor: theme.gray[300] }}>↑</kbd>{' '}
                  <kbd className="px-1.5 py-0.5 rounded border" style={{ borderColor: theme.gray[300] }}>↓</kbd> to navigate,{' '}
                  <kbd className="px-1.5 py-0.5 rounded border" style={{ borderColor: theme.gray[300] }}>Enter</kbd> to select,{' '}
                  <kbd className="px-1.5 py-0.5 rounded border" style={{ borderColor: theme.gray[300] }}>Esc</kbd> to close
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchModal;
